import {useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Grid} from '@mui/material';

import {HardFactsFieldsDegree} from './fields/HardFactsFieldsDegree';
import {HardFactsFieldsDepartment} from './fields/HardFactsFieldsDepartment';
import {HardFactsFieldsEmploymentType} from './fields/HardFactsFieldsEmploymentType';
import {HardFactsFieldsExperience} from './fields/HardFactsFieldsExperience';
import {HardFactsFieldsLanguages} from './fields/HardFactsFieldsLanguages';
import {HardFactsFieldsLocations} from './fields/HardFactsFieldsLocations';
import {HardFactsFieldsSalary} from './fields/HardFactsFieldsSalary';
import {HardFactsFieldsSubdepartment} from './fields/HardFactsFieldsSubdepartment';
import {HardFactsFieldsTitle} from './fields/HardFactsFieldsTitle';
import {HardFactsFieldsWorkMode} from './fields/HardFactsFieldsWorkMode';
import {CompanyContext} from '../../../../../../../../contexts/CompanyContext/CompanyContext';
import {SUPPORTED_WORK_MODES, SUPPORTED_DEGREE_LEVELS} from '../../../../../../../../lib/constants';
import {CompanyLocation} from '../../../../types/JobPostingsTypes';

interface HardFactsFieldsPropTypes {
  companyLocations: CompanyLocation[];
  formik: any;
  displayDefaultLocations: boolean;
}

export const HardFactsFields = ({companyLocations, formik, displayDefaultLocations}: HardFactsFieldsPropTypes) => {
  const {departments} = useContext(CompanyContext);
  const {t} = useTranslation();

  const workModeOptions = SUPPORTED_WORK_MODES.map((mode) => ({
    id: mode.id,
    label: t(`shared.workModes.${mode.id}`),
  }));

  const degreeOptions = SUPPORTED_DEGREE_LEVELS.map((level) => ({
    id: level.id,
    label: t(`shared.degrees.${level.id}`),
  }));

  const gridStyle = {
    display: 'flex',
  };

  const emptyState = {
    resourceId: '',
    name: {
      en: 'Please select a department first',
      de: 'Bitte wählen Sie zunächst eine Abteilung',
    },
  };

  const availableSubDepartments = useMemo(
    () =>
      (departments.data || [])
        .filter((dep) => {
          return !!formik.values.departments?.includes(dep.resourceId);
        })
        .map((dep) => {
          return dep.subDepartments || dep.subdepartments || [];
        })
        .flat(),
    [departments, formik.values.departments]
  );

  return (
    <>
      <Grid container spacing={1} display={'flex'} mb={5}>
        <Grid item xs={12} sm={12}>
          <HardFactsFieldsTitle />
        </Grid>

        <Grid item xs={12} sm={4} sx={{...gridStyle}} gap={1}>
          <HardFactsFieldsDepartment formik={formik} options={departments.data || []} />
        </Grid>

        <Grid item xs={12} sm={4} sx={{...gridStyle}} gap={1}>
          <HardFactsFieldsSubdepartment
            formik={formik}
            options={availableSubDepartments.length ? availableSubDepartments : [emptyState]}
          />
        </Grid>

        <Grid item xs={12} sm={4} sx={{...gridStyle}} gap={1}>
          <HardFactsFieldsLocations
            formik={formik}
            options={companyLocations}
            displayDefaultLocations={displayDefaultLocations}
          />
        </Grid>
      </Grid>

      <Grid container spacing={1} display={'flex'} mb={5}>
        <Grid item xs={12} md={6} sx={{...gridStyle}} gap={4}>
          <HardFactsFieldsSalary />
        </Grid>

        <Grid item xs={12} md={6} sx={{...gridStyle}} gap={4}>
          <HardFactsFieldsWorkMode options={workModeOptions} />
        </Grid>

        <Grid item xs={12} md={6} sx={{...gridStyle}} gap={4}>
          <HardFactsFieldsEmploymentType formik={formik} />
        </Grid>

        <Grid item xs={12} md={6} sx={{...gridStyle}} gap={4}>
          <HardFactsFieldsDegree options={degreeOptions} />
        </Grid>

        <Grid item xs={12} md={6} sx={{...gridStyle}} gap={4}>
          <HardFactsFieldsExperience />
        </Grid>

        <Grid item xs={12} md={6} sx={{...gridStyle}} gap={4}>
          <HardFactsFieldsLanguages formik={formik} />
        </Grid>
      </Grid>
    </>
  );
};
